/** PUT-AWAY / MOVE: SCAN DEVICE → SCAN LOCATION → saved automatically → ready for the next device (no Save button). */
import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, CheckCircle2 } from "lucide-react";
import { CameraButton } from "@/components/warehouse/CameraScan";
import { SmartLocationInput } from "@/components/warehouse/SmartLocationInput";

const msg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || e.message; };

export function PutawayFlow({ onStored, testid = "pa-flow" }) {
  const [dev, setDev] = useState("");
  const [unit, setUnit] = useState(null);
  const [last, setLast] = useState(null);
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const findDevice = async (code = dev) => {
    if (!code.trim()) return;
    try { const { data } = await api.get(`/warehouse-receiving/scan/${encodeURIComponent(code.trim())}`); if (data.kind !== "unit") { toast.error("Scan a device / inventory label first, then the location"); return; } setUnit(data.unit); }
    catch (e) { toast.error(msg(e)); }
  };
  const store = async (l) => {
    try {
      const { data } = await api.post("/warehouse-receiving/putaway", { code: unit.code, location_code: l.code });
      setLast({ code: unit.code, serial: unit.serial, type: data.type, from: data.from_friendly?.display || data.from, to: data.to_friendly?.display || data.to });
      setUnit(null); setDev(""); setN((x) => x + 1); onStored && onStored();
      setTimeout(() => ref.current?.focus(), 60);
    } catch (e) { toast.error(msg(e)); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Put away / move · scan device → scan location</div>
      {last && <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1.5 text-[12px] text-emerald-900" data-testid={`${testid}-stored`}><CheckCircle2 className="h-4 w-4" /><b>{last.type === "MOVE" ? "MOVED" : "STORED"}</b> {last.code}{last.serial ? ` · SN ${last.serial}` : ""} → {last.to}{last.from ? <span className="text-emerald-700"> (from {last.from})</span> : null}</div>}
      {!unit ? (
        <div className="flex gap-1.5">
          <div className="relative flex-1"><ScanLine className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" /><input ref={ref} autoFocus value={dev} onChange={(e) => setDev(e.target.value)} onKeyDown={(e) => e.key === "Enter" && findDevice()} placeholder="1 · Scan device / inventory label / serial"
            className="h-10 w-full rounded-lg border-2 border-slate-300 pl-8 pr-2 font-mono text-[12px] focus:border-slate-900 focus:outline-none" data-testid={`${testid}-device`} /></div>
          <CameraButton onCode={(c) => { setDev(c); findDevice(c); }} className="h-10" tid={`${testid}-device-camera`} />
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="text-[12px] text-slate-700" data-testid={`${testid}-unit`}><b>{unit.code}</b> · {unit.product_name}{unit.serial ? ` · SN ${unit.serial}` : ""} · now at <b>{unit.location_code || "no location"}</b> <button onClick={() => setUnit(null)} className="ml-1 text-[11px] text-slate-500 underline">change</button></div>
          <SmartLocationInput autoFocus onResolved={store} placeholder="2 · Scan the location — any format" testid={`${testid}-loc`} />
        </div>
      )}
      {n > 0 && <div className="text-[10.5px] text-slate-500" data-testid={`${testid}-count`}>{n} stored this session</div>}
    </div>
  );
}
